import { uiGet, uiMoney } from "./kit.js";
import { esc } from "./format.js";

/**
 * Launch Day (backend/launch_day.py): the moment a product goes on sale.
 * A sales counter ticking up through the first week, the hype meter your
 * marketing built, three critics' scores and the social feed, then what the
 * reviews do to the product. Fits the screen (max 90vh, scrolls inside) with
 * a visible close; motion is skipped when the player prefers reduced motion.
 */

const LD_TONE = { love: "pos", meh: "", hate: "neg" };

/** Opens the Launch Day screen; resolves when the player closes it. */
async function openLaunchDay(productId) {
  let d;
  try { d = await uiGet(`/launch-day?product_id=${productId}`); } catch (e) { return undefined; }
  const calm = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  const back = document.createElement("div");
  back.className = "modal-backdrop ld-backdrop";
  const maxDay = Math.max(1, ...d.first_week);
  back.innerHTML = `
    <div class="modal ld" role="dialog" aria-modal="true" aria-label="Launch Day: ${esc(d.product)}">
      <button type="button" class="ld-close" aria-label="Close" data-ld-close>✕</button>
      <div class="ld-kick">🚀 Launch Day · month ${d.month}</div>
      <h2>${esc(d.product)} is on sale</h2>
      <div class="ld-top">
        <div class="ld-counter"><span>First-week sales</span><b data-ld-count>$0</b><i data-ld-day>Day 1</i></div>
        <div class="ld-hype"><span>Hype</span>
          <div class="ld-hype-bar" role="meter" aria-valuenow="${d.hype}" aria-valuemin="0" aria-valuemax="100"><i data-ld-hype style="width:${calm ? d.hype : 0}%"></i></div>
          <em>${d.hype}/100 · ${d.marketing_budget ? `${uiMoney(d.marketing_budget, { compact: true })} of launch marketing` : "no launch marketing"}</em></div>
      </div>
      <div class="ld-week" aria-label="Sales by day">${d.first_week.map((v, i) => `<div class="ld-day" data-ld-bar="${i}" style="--h:${(100 * v / maxDay).toFixed(1)}%" title="Day ${i + 1}: ${uiMoney(v)}"><i></i><span>D${i + 1}</span></div>`).join("")}</div>
      <h3>What the critics say <span class="ld-score ld-${esc(d.verdict)}">${d.score}/10</span></h3>
      <div class="ld-critics">${d.critics.map((c) => `<article class="ld-critic"><b>${esc(c.outlet)}</b><span>${esc(c.line)}</span></article>`).join("")}</div>
      <h3>What people are posting</h3>
      <div class="ld-social">${d.social.map((s) => `<p class="ld-post ${LD_TONE[s.tone] || ""}"><b>${esc(s.handle)}</b> ${esc(s.text)} <em>♥ ${s.likes.toLocaleString("en-US")}</em></p>`).join("")}</div>
      <p class="ld-effect ${d.boost_pts > 0 ? "pos" : d.boost_pts < 0 ? "neg" : ""}">${esc(d.effect)}</p>
      <div class="modal-actions"><button type="button" class="primary" data-ld-close>Back to work</button></div>
    </div>`;
  document.body.appendChild(back);
  let done;
  const closed = new Promise((r) => { done = r; });
  const close = () => { back.remove(); done(); };
  back.querySelectorAll("[data-ld-close]").forEach((b) => b.addEventListener("click", close));
  back.addEventListener("click", (e) => { if (e.target === back) close(); });
  document.addEventListener("keydown", function esc_(e) { if (e.key === "Escape") { close(); document.removeEventListener("keydown", esc_); } });
  const count = back.querySelector("[data-ld-count]"), day = back.querySelector("[data-ld-day]");
  const bars = [...back.querySelectorAll("[data-ld-bar]")];
  if (calm) {
    count.textContent = uiMoney(d.first_week_total);
    day.textContent = "Week one";
    bars.forEach((b) => b.classList.add("on"));
    return closed;
  }
  requestAnimationFrame(() => { back.querySelector("[data-ld-hype]").style.width = `${d.hype}%`; });
  const DAY_MS = 520;
  const start = performance.now();
  const cum = d.first_week.reduce((a, v) => { a.push((a[a.length - 1] || 0) + v); return a; }, []);
  const tick = (t) => {
    if (!back.isConnected) return;
    const p = Math.min(7, (t - start) / DAY_MS);
    const i = Math.min(6, Math.floor(p));
    const within = p - i;
    const value = (i ? cum[i - 1] : 0) + d.first_week[i] * Math.min(1, within);
    count.textContent = uiMoney(p >= 7 ? d.first_week_total : value);
    day.textContent = p >= 7 ? "Week one" : `Day ${i + 1}`;
    bars.forEach((b, k) => b.classList.toggle("on", k <= i));
    if (p < 7) requestAnimationFrame(tick);
  };
  requestAnimationFrame(tick);
  return closed;
}

window.openLaunchDay = openLaunchDay;

export { openLaunchDay };
